import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Package, AlertTriangle, ArrowRightLeft, ClipboardCheck, Truck, TrendingUp, 
  Warehouse, Bell, Eye, ArrowRight, Clock, CheckCircle2, RefreshCw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { useAuth } from '@/contexts/AuthContext';
import { inventoryAPI, stockTransfersAPI, grnAPI, grnV2API } from '@/services/api';
import api from '@/services/api';
import { formatDate } from '@/utils/dateFormat';

const statusConfig = {
  new: { label: 'New', color: 'bg-purple-100 text-purple-700', priority: 1 },
  processing: { label: 'Processing', color: 'bg-amber-100 text-amber-700', priority: 2 },
  in_transit: { label: 'In Transit', color: 'bg-blue-100 text-blue-700', priority: 3 },
  discrepancy: { label: 'Discrepancy', color: 'bg-orange-100 text-orange-700', priority: 1 },
  received: { label: 'Received', color: 'bg-emerald-100 text-emerald-700', priority: 4 }
};

const WarehouseDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [stockOverview, setStockOverview] = useState(null);
  const [transferAlerts, setTransferAlerts] = useState(null);
  const [pendingGRNs, setPendingGRNs] = useState(0);
  const [pendingDispatch, setPendingDispatch] = useState({ count: 0, items: [] });
  
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const warehouseId = user?.entity_id;

  const fetchDashboardData = useCallback(async () => {
    if (!warehouseId) return;
    
    setLoading(true);
    try {
      // Fetch stock overview for this warehouse
      const stockRes = await inventoryAPI.getWarehouseStock(warehouseId, { view: 'active' });
      setStockOverview(stockRes.data);
      
      // Fetch transfer alerts (new workflow)
      try {
        const alertsRes = await stockTransfersAPI.getAlerts(warehouseId, 'warehouse');
        setTransferAlerts(alertsRes.data);
      } catch (e) {
        console.log('Transfer alerts API not available:', e);
      }
      
      // Fetch pending GRNs for this warehouse using V2 API (pending_verification status)
      try {
        const grnsRes = await grnV2API.listPendingVerification();
        setPendingGRNs(grnsRes.data?.length || 0);
      } catch (e) {
        // Fallback to legacy API
        try {
          const legacyRes = await grnAPI.list({ warehouse_id: warehouseId, status: 'draft' });
          setPendingGRNs(legacyRes.data?.length || 0);
        } catch (e2) {
          console.log('GRN API not available');
        }
      }
      
      // Fetch approved stock transfers waiting for pack/dispatch
      try {
        const dispatchRes = await api.get('/transfers/warehouse/alerts/pending-dispatch', { params: { limit: 10 } });
        setPendingDispatch(dispatchRes.data || { count: 0, items: [] });
      } catch (e) {
        console.log('Pending dispatch API not available:', e);
      }
      
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch dashboard data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [warehouseId, toast]);

  useEffect(() => {
    fetchDashboardData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchDashboardData, 30000);
    return () => clearInterval(interval);
  }, [fetchDashboardData]);

  if (loading) {
    return (
      <div className="p-6 lg:p-8 bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  const totalProducts = stockOverview?.summary?.total_items || 0;
  const totalQty = stockOverview?.summary?.total_quantity || 0;
  const totalValue = stockOverview?.summary?.total_value || 0;
  const lowStockCount = stockOverview?.summary?.low_stock_count || 0;
  
  // Transfer alerts
  const newTransfers = transferAlerts?.outgoing_pending?.filter(t => t.status === 'new') || [];
  const processingTransfers = transferAlerts?.outgoing_pending?.filter(t => t.status === 'processing') || [];
  const discrepancyTransfers = transferAlerts?.outgoing_pending?.filter(t => t.status === 'discrepancy') || [];
  const needsAction = newTransfers.length + discrepancyTransfers.length;

  return (
    <div className="p-6 lg:p-8 bg-gray-50 min-h-screen" data-testid="warehouse-dashboard">
      <Toaster />
      
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center">
            <Warehouse className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">{user?.entity_name || 'Warehouse'} Dashboard</h1>
            <p className="text-gray-500">Welcome back, {user?.full_name}</p>
          </div>
        </div>
        <Button variant="outline" onClick={fetchDashboardData} className="border-gray-300">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Pending Dispatch Alert — approved STOs waiting for pack/dispatch */}
      {pendingDispatch.count > 0 && (() => {
        const overdue = pendingDispatch.items.filter(r => {
          const h = (Date.now() - new Date(r.approved_at || r.created_at).getTime()) / 3600000;
          return h >= 24;
        });
        const hasOverdue = overdue.length > 0;
        return (
          <div
            onClick={() => navigate('/warehouse/stock-transfers')}
            className={`mb-6 rounded-xl border-l-4 shadow-sm p-4 cursor-pointer transition-colors ${hasOverdue ? 'border-red-500 bg-red-50 hover:bg-red-100' : 'border-amber-500 bg-amber-50 hover:bg-amber-100'}`}
            data-testid="pending-dispatch-card"
          >
            <div className="flex items-start gap-3">
              <Truck className={`w-6 h-6 mt-0.5 shrink-0 ${hasOverdue ? 'text-red-600' : 'text-amber-600'}`} />
              <div className="flex-1 min-w-0">
                <p className={`font-semibold ${hasOverdue ? 'text-red-800' : 'text-amber-800'}`}>
                  {pendingDispatch.count} approved stock transfer{pendingDispatch.count > 1 ? 's' : ''} waiting to process
                  {hasOverdue && (
                    <span className="ml-2 text-xs bg-red-200 text-red-800 px-2 py-0.5 rounded-full font-bold">
                      {overdue.length} overdue &gt;24h
                    </span>
                  )}
                </p>
                <ul className={`mt-1 text-sm space-y-0.5 ${hasOverdue ? 'text-red-700' : 'text-amber-700'}`}>
                  {pendingDispatch.items.slice(0, 5).map(r => {
                    const h = Math.max(0, Math.floor((Date.now() - new Date(r.approved_at).getTime()) / 60000));
                    const hh = Math.floor(h / 60);
                    const age = hh >= 24 ? `${Math.floor(hh / 24)}d ${hh % 24}h ago` : hh >= 1 ? `${hh}h ${h % 60}m ago` : `${h}m ago`;
                    const isOverdue = hh >= 24;
                    return (
                      <li key={r.id} className="truncate">
                        <span className="font-mono text-xs">{r.transfer_number}</span>
                        <span className="opacity-70"> → </span>
                        <span className="font-semibold">{r.destination_name}</span>
                        <span className="opacity-70"> ({r.destination_code})</span>
                        {r.destination_store_type === 'franchise' && (
                          <span className="ml-1 text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">Franchise</span>
                        )}
                        <span className="ml-1 text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded capitalize">{r.status}</span>
                        <span className={`ml-1 text-xs px-1.5 py-0.5 rounded font-medium ${isOverdue ? 'bg-red-100 text-red-700 border border-red-300' : 'bg-amber-100 text-amber-700'}`}>
                          {isOverdue ? 'overdue · ' : ''}{age}
                        </span>
                        <span className="opacity-70"> · {r.total_items} item{r.total_items !== 1 ? 's' : ''}</span>
                      </li>
                    );
                  })}
                  {pendingDispatch.count > 5 && (
                    <li className="text-xs opacity-75">… and {pendingDispatch.count - 5} more</li>
                  )}
                </ul>
                <p className="text-xs opacity-75 mt-2">Click to open Stock Transfers</p>
              </div>
              <Badge className={hasOverdue ? 'bg-red-600 text-white' : 'bg-amber-600 text-white'} data-testid="pending-dispatch-count">
                {pendingDispatch.count}
              </Badge>
            </div>
          </div>
        );
      })()}

      {/* Transfer Alert Banner */}
      {needsAction > 0 && (
        <div className="mb-6 p-4 bg-gradient-to-r from-purple-500 to-indigo-500 rounded-xl text-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <Bell className="w-5 h-5 animate-pulse" />
            </div>
            <div>
              <p className="font-semibold">Action Required: {needsAction} Transfer(s)</p>
              <p className="text-sm text-white/80">
                {newTransfers.length > 0 && `${newTransfers.length} new transfer(s) from HQ. `}
                {discrepancyTransfers.length > 0 && `${discrepancyTransfers.length} discrepancy to resolve.`}
              </p>
            </div>
          </div>
          <Button 
            onClick={() => navigate('/warehouse/transfers')}
            className="bg-white text-purple-600 hover:bg-white/90"
          >
            View Transfers
          </Button>
        </div>
      )}

      {/* Pending GRN Alert Banner */}
      {pendingGRNs > 0 && (
        <div className="mb-6 p-4 bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl text-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <ClipboardCheck className="w-5 h-5 animate-pulse" />
            </div>
            <div>
              <p className="font-semibold">Pending Verification: {pendingGRNs} GRN(s)</p>
              <p className="text-sm text-white/80">
                Incoming stock awaiting your verification and approval
              </p>
            </div>
          </div>
          <Button 
            onClick={() => navigate('/warehouse/grn/pending')}
            className="bg-white text-amber-600 hover:bg-white/90"
          >
            Verify Now
          </Button>
        </div>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Products</p>
                <p className="text-2xl font-bold text-gray-800">{totalProducts}</p>
                <p className="text-xs text-gray-400">{totalQty.toLocaleString()} units</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <Package className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Stock Value</p>
                <p className="text-2xl font-bold text-teal-600">₹{totalValue.toLocaleString()}</p>
              </div>
              <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-teal-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Low Stock Items</p>
                <p className="text-2xl font-bold text-amber-600">{lowStockCount}</p>
              </div>
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`cursor-pointer hover:shadow-md transition-shadow ${pendingGRNs > 0 ? 'border-purple-300 bg-purple-50 border-2' : 'bg-white border-gray-200'} shadow-sm`}
          onClick={() => navigate('/warehouse/grn/pending')}
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Pending GRNs</p>
                <p className={`text-2xl font-bold ${pendingGRNs > 0 ? 'text-purple-600' : 'text-gray-600'}`}>{pendingGRNs}</p>
                {pendingGRNs > 0 && <p className="text-xs text-purple-500">Click to verify</p>}
              </div>
              <div className={`w-12 h-12 ${pendingGRNs > 0 ? 'bg-purple-200' : 'bg-purple-100'} rounded-full flex items-center justify-center`}>
                <ClipboardCheck className={`w-6 h-6 ${pendingGRNs > 0 ? 'text-purple-700' : 'text-purple-600'}`} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={`border-2 ${needsAction > 0 ? 'border-purple-300 bg-purple-50' : 'bg-white border-gray-200'} shadow-sm`}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Transfer Alerts</p>
                <p className={`text-2xl font-bold ${needsAction > 0 ? 'text-purple-600' : 'text-gray-600'}`}>{needsAction}</p>
                <p className="text-xs text-gray-400">Needs action</p>
              </div>
              <div className={`w-12 h-12 ${needsAction > 0 ? 'bg-purple-200' : 'bg-gray-100'} rounded-full flex items-center justify-center`}>
                <ArrowRightLeft className={`w-6 h-6 ${needsAction > 0 ? 'text-purple-600' : 'text-gray-600'}`} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Transfer Orders Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* New Transfer Orders (From HQ) */}
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardHeader className="pb-2 border-b border-gray-100">
            <CardTitle className="text-lg text-gray-800 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-purple-500" />
                New Transfer Orders
              </div>
              {newTransfers.length > 0 && (
                <Badge className="bg-purple-100 text-purple-700">{newTransfers.length} New</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            {newTransfers.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <CheckCircle2 className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p>No new transfer orders</p>
              </div>
            ) : (
              <div className="space-y-3">
                {newTransfers.slice(0, 5).map((transfer) => (
                  <div 
                    key={transfer.id} 
                    className="p-3 bg-purple-50 rounded-lg border border-purple-100 hover:bg-purple-100 transition-colors cursor-pointer"
                    onClick={() => navigate('/warehouse/transfers')}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-purple-800">{transfer.transfer_number}</span>
                      <Badge className={statusConfig[transfer.status]?.color}>{statusConfig[transfer.status]?.label}</Badge>
                    </div>
                    <div className="text-sm text-gray-600">
                      To: {transfer.to_location_name}
                    </div>
                    <div className="text-xs text-gray-400 mt-1">
                      {transfer.total_items} items • {transfer.total_quantity} units
                    </div>
                  </div>
                ))}
                {newTransfers.length > 5 && (
                  <Button 
                    variant="ghost" 
                    className="w-full text-purple-600"
                    onClick={() => navigate('/warehouse/transfers')}
                  >
                    View All ({newTransfers.length}) <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* In-Process & In-Transit Transfers */}
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardHeader className="pb-2 border-b border-gray-100">
            <CardTitle className="text-lg text-gray-800 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Truck className="w-5 h-5 text-blue-500" />
                Active Transfers
              </div>
              {(processingTransfers.length > 0 || discrepancyTransfers.length > 0) && (
                <Badge className="bg-blue-100 text-blue-700">
                  {processingTransfers.length + discrepancyTransfers.length} Active
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            {processingTransfers.length === 0 && discrepancyTransfers.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Truck className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p>No active transfers</p>
              </div>
            ) : (
              <div className="space-y-3">
                {discrepancyTransfers.map((transfer) => (
                  <div 
                    key={transfer.id} 
                    className="p-3 bg-orange-50 rounded-lg border border-orange-200 hover:bg-orange-100 transition-colors cursor-pointer"
                    onClick={() => navigate('/warehouse/transfers')}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-orange-800">{transfer.transfer_number}</span>
                      <Badge className="bg-orange-100 text-orange-700">Discrepancy</Badge>
                    </div>
                    <div className="text-sm text-orange-600 font-medium">
                      ⚠️ Needs Resolution
                    </div>
                  </div>
                ))}
                {processingTransfers.map((transfer) => (
                  <div 
                    key={transfer.id} 
                    className="p-3 bg-amber-50 rounded-lg border border-amber-100 hover:bg-amber-100 transition-colors cursor-pointer"
                    onClick={() => navigate('/warehouse/transfers')}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-amber-800">{transfer.transfer_number}</span>
                      <Badge className={statusConfig[transfer.status]?.color}>{statusConfig[transfer.status]?.label}</Badge>
                    </div>
                    <div className="text-sm text-gray-600">
                      To: {transfer.to_location_name}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card className="bg-white border-gray-200 shadow-sm">
        <CardHeader className="pb-2 border-b border-gray-100">
          <CardTitle className="text-lg text-gray-800">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Button 
              variant="outline" 
              className="h-auto py-4 flex flex-col items-center gap-2 border-gray-200 hover:bg-teal-50 hover:border-teal-300"
              onClick={() => navigate('/warehouse/stock')}
            >
              <Package className="w-6 h-6 text-teal-600" />
              <span className="text-sm">View Stock</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-auto py-4 flex flex-col items-center gap-2 border-gray-200 hover:bg-blue-50 hover:border-blue-300"
              onClick={() => navigate('/warehouse/transfers')}
            >
              <ArrowRightLeft className="w-6 h-6 text-blue-600" />
              <span className="text-sm">Transfers</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-auto py-4 flex flex-col items-center gap-2 border-gray-200 hover:bg-purple-50 hover:border-purple-300"
              onClick={() => navigate('/warehouse/grn/pending')}
            >
              <ClipboardCheck className="w-6 h-6 text-purple-600" />
              <span className="text-sm">GRN Verify</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-auto py-4 flex flex-col items-center gap-2 border-gray-200 hover:bg-amber-50 hover:border-amber-300"
              onClick={() => navigate('/warehouse/stock?filter=low_stock')}
            >
              <AlertTriangle className="w-6 h-6 text-amber-600" />
              <span className="text-sm">Low Stock</span>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default WarehouseDashboard;
